import { LINE_SEGMENTS } from '../../api/monitor';
import type { LineStatusFilter, OperatorOption } from '../../api/monitor';
import { SearchSelect } from '../../components/ui/SearchSelect';

interface LineFiltersProps {
  operators: OperatorOption[];
  /** Nomes de master presentes na base de linhas — as opções do filtro. */
  masters: string[];
  /** `null` = todas. */
  selected: string | null;
  /** Nomes de master filtrados; vazio = todos. */
  selectedMasters: string[];
  status: LineStatusFilter;
  /** Segmentos Claro cujo consumo é exibido; vazio = consumo total. */
  segments: string[];
  query: string;
  onSelect: (code: string | null) => void;
  onMastersChange: (names: string[]) => void;
  onStatusChange: (status: LineStatusFilter) => void;
  onSegmentsChange: (segments: string[]) => void;
  onQueryChange: (value: string) => void;
}

/** As situações, na ordem da tela; "Ativas" vem primeiro porque é o padrão. */
const STATUS_OPTIONS: Array<{ value: LineStatusFilter; label: string }> = [
  { value: 'active', label: 'Ativas' },
  { value: 'unassigned', label: 'Sem cliente' },
  { value: 'inactive', label: 'Inativas' },
  { value: 'all', label: 'Todas' },
];

function chipClass(selected: boolean): string {
  return `rounded-full border px-3.5 py-2 text-[12.5px] font-bold ${
    selected ? 'border-navy-800 bg-navy-800 text-white' : 'border-line-300 text-ink-600 bg-white'
  }`;
}

/**
 * Busca por número + pílulas de operadora + filtro de master + lista de situação.
 *
 * As pílulas vêm de `/operators`, e não de uma lista fixa Claro/Vivo: operadora
 * é cadastro, e a tela deve acompanhar quem estiver ativo no banco.
 *
 * O master é um select com busca, e não pílulas como a operadora: operadoras
 * são duas, masters são centenas — o que se faz é procurar um pelo nome.
 */
export function LineFilters({
  operators,
  masters,
  selected,
  selectedMasters,
  status,
  segments,
  query,
  onSelect,
  onMastersChange,
  onStatusChange,
  onSegmentsChange,
  onQueryChange,
}: LineFiltersProps) {
  const chips: Array<{ code: string | null; label: string }> = [
    { code: null, label: 'Todas' },
    ...operators.map((operator) => ({ code: operator.code, label: operator.name })),
  ];

  return (
    <div className="mb-3 flex flex-wrap items-center gap-2.5">
      <input
        type="search"
        value={query}
        onChange={(event) => onQueryChange(event.target.value)}
        placeholder="Buscar por número ou master…"
        aria-label="Buscar por número ou master"
        className="border-line-300 focus:border-navy-800 min-w-[200px] flex-1 rounded-[10px] border bg-white px-3.5 py-2.5 text-[14px] outline-none focus:ring-2 focus:ring-[#00244733]"
      />

      <div className="flex gap-1.5">
        {chips.map((chip) => {
          const active = chip.code === selected;

          return (
            <button
              key={chip.code ?? 'all'}
              type="button"
              onClick={() => onSelect(chip.code)}
              aria-pressed={active}
              className={chipClass(active)}
            >
              {chip.label}
            </button>
          );
        })}
      </div>

      <SearchSelect
        size="sm"
        multiple
        values={selectedMasters}
        onValuesChange={onMastersChange}
        options={masters.map((name) => ({ value: name, label: name }))}
        clearable
        placeholder="Todos os masters"
        searchPlaceholder="Buscar master…"
        aria-label="Filtrar por master"
      />

      {/* Lista, e não pílulas: com as pílulas de operadora ao lado, eram dois
          botões "Todas" na mesma linha — um de operadora, outro de situação. */}
      <label className="border-line-300 text-ink-600 flex items-center gap-1.5 rounded-full border bg-white px-3.5 py-2 text-[12.5px] font-bold">
        Situação:
        <SearchSelect
          variant="pill"
          value={status}
          onChange={(value) => onStatusChange(value as LineStatusFilter)}
          options={STATUS_OPTIONS}
          aria-label="Situação da linha"
        />
      </label>

      {/* Só a Claro salva a quebra por grupo, então o seletor só aparece quando
          a operadora filtrada é a Claro. Em "Todas" ou na Vivo ele fica fora,
          porque não há esse recorte para elas. Mais de um segmento soma o
          consumo dos escolhidos. */}
      {selected === 'claro' ? (
        <label className="border-line-300 text-ink-600 flex items-center gap-1.5 rounded-full border bg-white px-3.5 py-2 text-[12.5px] font-bold">
          Segmento:
          <SearchSelect
            variant="pill"
            multiple
            values={segments}
            onValuesChange={onSegmentsChange}
            options={LINE_SEGMENTS}
            placeholder="Consumo total"
            clearable
            aria-label="Segmento de consumo"
          />
        </label>
      ) : null}
    </div>
  );
}
